/**
 * Geocoder for Eversend transaction identifiers.
 *
 * The source table mixes three ways of identifying origin/destination:
 *   1. ISO-2 country code (NG, KE, UG, CM, GH, RW, ZA, ...)
 *   2. ISO-4217 currency code (NGN, KES, XAF, XOF, GBP, USD, ...)
 *   3. Currency-pair in `transaction_type` (e.g. "NGN_GBP" → NGN / GBP)
 *
 * We resolve any of the three to a primary city with (lat, lng).
 * For intra-country transactions (NG → NG), we pick a random secondary
 * city pair so the arc has visible length on the map.
 */

export type GeoCity = {
  lat: number;
  lng: number;
  city: string;
  country: string; // ISO-2
};

/** ISO-2 country → primary fintech city. */
const COUNTRY_PRIMARY: Record<string, GeoCity> = {
  NG: { lat: 6.5244, lng: 3.3792, city: "Lagos", country: "NG" },
  KE: { lat: -1.2921, lng: 36.8219, city: "Nairobi", country: "KE" },
  UG: { lat: 0.3476, lng: 32.5825, city: "Kampala", country: "UG" },
  RW: { lat: -1.9706, lng: 30.1044, city: "Kigali", country: "RW" },
  TZ: { lat: -6.7924, lng: 39.2083, city: "Dar es Salaam", country: "TZ" },
  GH: { lat: 5.6037, lng: -0.187, city: "Accra", country: "GH" },
  ZA: { lat: -26.2041, lng: 28.0473, city: "Johannesburg", country: "ZA" },
  CM: { lat: 4.0511, lng: 9.7679, city: "Douala", country: "CM" },
  CI: { lat: 5.3599, lng: -4.0083, city: "Abidjan", country: "CI" },
  SN: { lat: 14.7167, lng: -17.4677, city: "Dakar", country: "SN" },
  EG: { lat: 30.0444, lng: 31.2357, city: "Cairo", country: "EG" },
  ET: { lat: 9.03, lng: 38.74, city: "Addis Ababa", country: "ET" },
  MA: { lat: 33.5731, lng: -7.5898, city: "Casablanca", country: "MA" },
  CG: { lat: -4.2634, lng: 15.2429, city: "Brazzaville", country: "CG" },
  GA: { lat: 0.4162, lng: 9.4673, city: "Libreville", country: "GA" },
  GQ: { lat: 3.75, lng: 8.7833, city: "Malabo", country: "GQ" },
  TD: { lat: 12.1348, lng: 15.0557, city: "N'Djamena", country: "TD" },
  CF: { lat: 4.3947, lng: 18.5582, city: "Bangui", country: "CF" },
  BJ: { lat: 6.3703, lng: 2.3912, city: "Cotonou", country: "BJ" },
  BF: { lat: 12.3714, lng: -1.5197, city: "Ouagadougou", country: "BF" },
  ML: { lat: 12.6392, lng: -8.0029, city: "Bamako", country: "ML" },
  TG: { lat: 6.1725, lng: 1.2314, city: "Lomé", country: "TG" },
  NE: { lat: 13.5117, lng: 2.1251, city: "Niamey", country: "NE" },

  GB: { lat: 51.5074, lng: -0.1278, city: "London", country: "GB" },
  FR: { lat: 48.8566, lng: 2.3522, city: "Paris", country: "FR" },
  DE: { lat: 52.52, lng: 13.405, city: "Berlin", country: "DE" },
  NL: { lat: 52.3676, lng: 4.9041, city: "Amsterdam", country: "NL" },
  IE: { lat: 53.3498, lng: -6.2603, city: "Dublin", country: "IE" },
  ES: { lat: 40.4168, lng: -3.7038, city: "Madrid", country: "ES" },
  IT: { lat: 41.9028, lng: 12.4964, city: "Rome", country: "IT" },
  BE: { lat: 50.8503, lng: 4.3517, city: "Brussels", country: "BE" },
  CH: { lat: 47.3769, lng: 8.5417, city: "Zurich", country: "CH" },

  US: { lat: 40.7128, lng: -74.006, city: "New York", country: "US" },
  CA: { lat: 43.6532, lng: -79.3832, city: "Toronto", country: "CA" },

  AE: { lat: 25.2048, lng: 55.2708, city: "Dubai", country: "AE" },
  IN: { lat: 19.076, lng: 72.8777, city: "Mumbai", country: "IN" },
  CN: { lat: 22.5431, lng: 114.0579, city: "Shenzhen", country: "CN" },
  SG: { lat: 1.3521, lng: 103.8198, city: "Singapore", country: "SG" },
  TR: { lat: 41.0082, lng: 28.9784, city: "Istanbul", country: "TR" },
  HK: { lat: 22.3193, lng: 114.1694, city: "Hong Kong", country: "HK" },
  JP: { lat: 35.6762, lng: 139.6503, city: "Tokyo", country: "JP" },
  AU: { lat: -33.8688, lng: 151.2093, city: "Sydney", country: "AU" },
};

/** ISO-4217 currency → ISO-2 country. Multi-country currencies pick the largest market. */
const CURRENCY_TO_COUNTRY: Record<string, string> = {
  NGN: "NG",
  KES: "KE",
  UGX: "UG",
  RWF: "RW",
  TZS: "TZ",
  GHS: "GH",
  ZAR: "ZA",
  EGP: "EG",
  ETB: "ET",
  MAD: "MA",
  // CFA franc zones — pick largest economies as proxies.
  XAF: "CM", // Central: Cameroon
  XOF: "CI", // West: Côte d'Ivoire
  USD: "US",
  GBP: "GB",
  EUR: "FR",
  CAD: "CA",
  AED: "AE",
  INR: "IN",
  CNY: "CN",
  SGD: "SG",
  TRY: "TR",
  HKD: "HK",
  JPY: "JP",
  AUD: "AU",
  // Crypto / synthetic — collapse to a nominal "offshore" coord.
  BTC: "CH", // treat as Zurich treasury proxy
  USDT: "CH",
  USDC: "CH",
  XDT: "CH", // stablecoin-ish
};

/** Alternate cities inside a country, for intra-country arcs. */
const COUNTRY_SECONDARIES: Record<string, GeoCity[]> = {
  NG: [
    { lat: 9.0579, lng: 7.4951, city: "Abuja", country: "NG" },
    { lat: 12.0022, lng: 8.592, city: "Kano", country: "NG" },
    { lat: 4.8156, lng: 7.0498, city: "Port Harcourt", country: "NG" },
    { lat: 7.3776, lng: 3.947, city: "Ibadan", country: "NG" },
  ],
  KE: [
    { lat: -4.0435, lng: 39.6682, city: "Mombasa", country: "KE" },
    { lat: -0.0917, lng: 34.768, city: "Kisumu", country: "KE" },
    { lat: -0.5143, lng: 35.2698, city: "Nakuru", country: "KE" },
  ],
  UG: [
    { lat: 0.6122, lng: 30.6575, city: "Mbarara", country: "UG" },
    { lat: 2.7778, lng: 32.2881, city: "Gulu", country: "UG" },
    { lat: 0.4478, lng: 33.2042, city: "Jinja", country: "UG" },
  ],
  RW: [{ lat: -2.596, lng: 29.7393, city: "Butare", country: "RW" }],
  TZ: [
    { lat: -6.163, lng: 35.7516, city: "Dodoma", country: "TZ" },
    { lat: -3.3869, lng: 36.683, city: "Arusha", country: "TZ" },
  ],
  GH: [
    { lat: 6.6885, lng: -1.6244, city: "Kumasi", country: "GH" },
    { lat: 4.9016, lng: -1.7831, city: "Takoradi", country: "GH" },
  ],
  ZA: [
    { lat: -33.9249, lng: 18.4241, city: "Cape Town", country: "ZA" },
    { lat: -29.8587, lng: 31.0218, city: "Durban", country: "ZA" },
  ],
  CM: [
    { lat: 3.848, lng: 11.5021, city: "Yaoundé", country: "CM" },
    { lat: 5.963, lng: 10.159, city: "Bamenda", country: "CM" },
  ],
  CI: [{ lat: 6.8276, lng: -5.2893, city: "Yamoussoukro", country: "CI" }],
  SN: [{ lat: 14.6928, lng: -17.4467, city: "Guédiawaye", country: "SN" }],
  US: [
    { lat: 33.749, lng: -84.388, city: "Atlanta", country: "US" },
    { lat: 32.7767, lng: -96.797, city: "Dallas", country: "US" },
    { lat: 37.7749, lng: -122.4194, city: "San Francisco", country: "US" },
    { lat: 29.7604, lng: -95.3698, city: "Houston", country: "US" },
  ],
  GB: [
    { lat: 53.4808, lng: -2.2426, city: "Manchester", country: "GB" },
    { lat: 52.4862, lng: -1.8904, city: "Birmingham", country: "GB" },
  ],
};

/**
 * ISO-3 → ISO-2 country code mapping.
 * `fct_virtual_cards__spend.merchant_country` uses ISO-3 (USA, NLD, IRL, SGP, …)
 * while `fct_transactions.source_country_code` uses ISO-2. Both flow through
 * this resolver so we need to handle both.
 */
const ISO3_TO_ISO2: Record<string, string> = {
  USA: "US", CAN: "CA", MEX: "MX",
  GBR: "GB", IRL: "IE", FRA: "FR", DEU: "DE", NLD: "NL", BEL: "BE",
  ESP: "ES", ITA: "IT", CHE: "CH", AUT: "AT", SWE: "SE", NOR: "NO",
  DNK: "DK", FIN: "FI", POL: "PL", PRT: "PT", GRC: "GR", CZE: "CZ",
  ARE: "AE", SAU: "SA", TUR: "TR", ISR: "IL", QAT: "QA", KWT: "KW",
  CHN: "CN", HKG: "HK", JPN: "JP", KOR: "KR", IND: "IN", SGP: "SG",
  MYS: "MY", THA: "TH", IDN: "ID", PHL: "PH", VNM: "VN",
  AUS: "AU", NZL: "NZ",
  NGA: "NG", KEN: "KE", UGA: "UG", RWA: "RW", TZA: "TZ", ETH: "ET",
  GHA: "GH", CMR: "CM", CIV: "CI", SEN: "SN", BFA: "BF", MLI: "ML",
  BEN: "BJ", TGO: "TG", NER: "NE", ZAF: "ZA", EGY: "EG", MAR: "MA",
  COG: "CG", GAB: "GA", GNQ: "GQ", TCD: "TD", CAF: "CF",
  BRA: "BR", ARG: "AR", CHL: "CL", COL: "CO", PER: "PE",
  RUS: "RU", UKR: "UA",
};

/** Normalize any ISO country code to ISO-2. Passes through already-ISO-2 codes. */
function toIso2(code: string): string {
  if (code.length === 3 && ISO3_TO_ISO2[code]) return ISO3_TO_ISO2[code];
  return code;
}

/** Resolve any identifier (country code or currency code) to a primary city. */
export function resolvePrimary(code: string | null | undefined): GeoCity | null {
  if (!code) return null;
  const up = toIso2(code.toUpperCase());
  if (COUNTRY_PRIMARY[up]) return COUNTRY_PRIMARY[up];
  const iso = CURRENCY_TO_COUNTRY[up];
  if (iso && COUNTRY_PRIMARY[iso]) return COUNTRY_PRIMARY[iso];
  return null;
}

/**
 * Resolve an origin/destination pair. If both resolve to the same country,
 * swap the destination to a secondary city so the arc has visible length.
 *
 * `seed` is typically the transaction_id — ensures the same tx always picks
 * the same secondary city on replay (no flickering on re-renders).
 */
export function resolveCorridor(
  originCode: string | null | undefined,
  destCode: string | null | undefined,
  seed = "",
): { origin: GeoCity; dest: GeoCity } | null {
  const a = resolvePrimary(originCode);
  const b = resolvePrimary(destCode);
  if (!a || !b) return null;

  if (a.country === b.country) {
    const secondaries = COUNTRY_SECONDARIES[a.country];
    if (secondaries && secondaries.length) {
      const idx = hash(seed) % secondaries.length;
      return { origin: a, dest: secondaries[idx] };
    }
    // No secondaries configured — nudge the destination slightly so the arc
    // at least renders as a visible micro-arc.
    return {
      origin: a,
      dest: { ...b, lat: b.lat + 0.6, lng: b.lng + 0.6 },
    };
  }
  return { origin: a, dest: b };
}

function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

/**
 * Card creation events have no user geography in the source table.
 * We seed each event to a random African hub so the "+" bursts appear
 * across Eversend's actual user-base footprint rather than all at one point.
 * Deterministic per event.id so the placement is stable across refreshes.
 */
const HUB_CITIES: GeoCity[] = [
  COUNTRY_PRIMARY.NG, // Lagos
  COUNTRY_PRIMARY.KE, // Nairobi
  COUNTRY_PRIMARY.UG, // Kampala
  COUNTRY_PRIMARY.GH, // Accra
  COUNTRY_PRIMARY.RW, // Kigali
  COUNTRY_PRIMARY.ZA, // Johannesburg
  COUNTRY_PRIMARY.CM, // Douala
  COUNTRY_PRIMARY.CI, // Abidjan
  COUNTRY_PRIMARY.SN, // Dakar
  COUNTRY_PRIMARY.TZ, // Dar es Salaam
];

export function seededHubCity(seed: string): GeoCity {
  return HUB_CITIES[hash(seed) % HUB_CITIES.length];
}
